import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, Trash2, ClipboardPaste, AlertTriangle } from "lucide-react";

const FIELDS = ["id", "name", "kind", "attending", "table", "starter_id", "main_id", "dessert_id",
  "allergies", "diet_note", "exception_requested"];

const strip = (g) => FIELDS.reduce((acc, k) => {
  if (g[k] !== undefined) acc[k] = g[k];
  return acc;
}, {});

const labelMap = (items) => Object.fromEntries((items || []).map((i) => [i.id, i.label]));

export default function GuestsTab({ wedding, catalog, patch }) {
  const [guests, setGuests] = useState(wedding.guests || []);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("adult");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [bulk, setBulk] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const dirty = useRef(false);
  const timer = useRef(null);
  const locked = wedding.locked;

  useEffect(() => {
    if (!dirty.current) setGuests(wedding.guests || []);
  }, [wedding.guests]);

  const commit = (next) => {
    setGuests(next);
    dirty.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const ok = await patch({ guests: next.map(strip) });
      if (ok) dirty.current = false;
    }, 700);
  };

  const sel = wedding.menu_selection || {};
  const sc = wedding.service_config || {};
  const adultServices = sc.adult_services || ["starter", "main", "dessert"];
  const childServices = sc.child_services || ["main", "dessert"];
  const pools = useMemo(() => {
    const all = {
      starter: labelMap(catalog.starters),
      main: labelMap(catalog.mains),
      dessert: labelMap(catalog.desserts),
    };
    const pick = (ids, map) => (ids || []).map((id) => ({ id, label: map[id] || "Choix" }));
    const childMains = (sel.child_mains || []).length ? sel.child_mains : sel.mains;
    const childDesserts = (sel.child_desserts || []).length ? sel.child_desserts : sel.desserts;
    return {
      adult: {
        starter: pick(sel.starters, all.starter),
        main: pick(sel.mains, all.main),
        dessert: pick(sel.desserts, all.dessert),
      },
      child: {
        main: pick(childMains, all.main),
        dessert: pick(childDesserts, all.dessert),
      },
    };
  }, [catalog, sel]);

  const add = () => {
    if (!name.trim()) return;
    commit([...guests, {
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: name.trim(), kind, attending: true, table: "", starter_id: "", main_id: "",
      dessert_id: "", allergies: "", diet_note: "", exception_requested: false,
    }]);
    setName("");
  };

  const addBulk = () => {
    const lines = bulk.split("\n").map((l) => l.trim()).filter(Boolean).slice(0, 300);
    if (!lines.length) return;
    commit([...guests, ...lines.map((l, i) => {
      const isChild = /\(enfant\)|\benfant\b/i.test(l);
      return {
        id: `tmp-${Date.now()}-${i}`,
        name: l.replace(/\(enfant\)/i, "").trim(),
        kind: isChild ? "child" : "adult",
        attending: true, table: "", starter_id: "", main_id: "", dessert_id: "",
        allergies: "", diet_note: "", exception_requested: false,
      };
    })]);
    setBulk("");
    setShowBulk(false);
  };

  const update = (id, patchObj) => commit(guests.map((g) => (g.id === id ? { ...g, ...patchObj } : g)));
  const remove = (g) => {
    if (!window.confirm(`Supprimer ${g.name} de la liste ?`)) return;
    commit(guests.filter((x) => x.id !== g.id));
  };

  const isIncomplete = (g) => {
    if (!g.attending) return false;
    const needed = g.kind === "child" ? childServices : adultServices;
    return needed.some((s) => !g[{ starter: "starter_id", main: "main_id", dessert: "dessert_id" }[s]]);
  };

  const visible = guests.filter((g) => {
    if (query && !g.name.toLowerCase().includes(query.toLowerCase())) return false;
    if (filter === "children") return g.kind === "child";
    if (filter === "incomplete") return isIncomplete(g);
    if (filter === "allergies") return (g.allergies || "").trim() || g.exception_requested;
    return true;
  });

  const counts = {
    adults: guests.filter((g) => g.attending && g.kind !== "child").length,
    children: guests.filter((g) => g.attending && g.kind === "child").length,
    absent: guests.filter((g) => !g.attending).length,
    incomplete: guests.filter(isIncomplete).length,
  };

  const noProposals = (adultServices.includes("main") && !(sel.mains || []).length)
    || (adultServices.includes("dessert") && !(sel.desserts || []).length);

  return (
    <div data-testid="guests-tab" className="space-y-6">
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.16em] text-[#697585]">
        <span data-testid="guests-count-adults">{counts.adults} adultes</span>
        <span data-testid="guests-count-children">{counts.children} enfants</span>
        <span>{counts.absent} absents</span>
        <span className={counts.incomplete ? "text-[#B8956A]" : ""} data-testid="guests-count-incomplete">
          {counts.incomplete} incomplets
        </span>
      </div>

      {noProposals && (
        <p data-testid="guests-no-proposals" className="flex gap-2 text-sm text-[#B8956A] border-l-2 border-[#B8956A] pl-3">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          Commencez par retenir vos propositions dans l'onglet « Nos menus » : vos invités choisiront parmi celles-ci.
        </p>
      )}

      {!locked && (
        <div className="bg-white border border-[#D9D3C5] p-5 md:p-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="guest-name-input"
              className="field flex-1"
              placeholder="Nom de l'invité"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            <select data-testid="guest-kind-select" className="field sm:w-40" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="adult">Adulte</option>
              <option value="child">Enfant</option>
            </select>
            <button data-testid="guest-add-btn" className="btn-primary inline-flex items-center gap-2" onClick={add}>
              <Plus size={14} /> Ajouter
            </button>
          </div>
          <button data-testid="guest-bulk-toggle" className="mt-4 text-xs uppercase tracking-[0.16em] text-[#B8956A] inline-flex items-center gap-2" onClick={() => setShowBulk((v) => !v)}>
            <ClipboardPaste size={14} /> Coller une liste
          </button>
          {showBulk && (
            <div className="mt-4">
              <textarea
                data-testid="guest-bulk-textarea"
                className="field min-h-[140px] w-full"
                placeholder={"Un nom par ligne\nAjoutez (enfant) après le nom pour un enfant"}
                value={bulk}
                onChange={(e) => setBulk(e.target.value)}
              />
              <button data-testid="guest-bulk-submit" className="btn-ghost mt-3" onClick={addBulk}>Ajouter la liste</button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#697585]" />
          <input data-testid="guest-search" className="field w-full pl-9" placeholder="Rechercher un invité" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <select data-testid="guest-filter" className="field sm:w-56" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">Tous les invités</option>
          <option value="children">Enfants</option>
          <option value="incomplete">Choix incomplets</option>
          <option value="allergies">Allergies / régimes</option>
        </select>
      </div>

      <ul className="space-y-3">
        {visible.map((g) => {
          const pool = g.kind === "child" ? pools.child : pools.adult;
          return (
            <li key={g.id} data-testid={`guest-row-${g.id}`} className={`bg-white border p-5 ${isIncomplete(g) ? "border-[#B8956A]/60" : "border-[#D9D3C5]"}`}>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  data-testid={`guest-name-${g.id}`}
                  className="field flex-1 min-w-[180px]"
                  value={g.name}
                  disabled={locked}
                  onChange={(e) => update(g.id, { name: e.target.value })}
                />
                <select data-testid={`guest-kind-${g.id}`} className="field w-32" value={g.kind} disabled={locked} onChange={(e) => update(g.id, { kind: e.target.value })}>
                  <option value="adult">Adulte</option>
                  <option value="child">Enfant</option>
                </select>
                <select data-testid={`guest-attending-${g.id}`} className="field w-32" value={g.attending ? "1" : "0"} disabled={locked} onChange={(e) => update(g.id, { attending: e.target.value === "1" })}>
                  <option value="1">Présent</option>
                  <option value="0">Absent</option>
                </select>
                <input data-testid={`guest-table-${g.id}`} className="field w-28" placeholder="Table" value={g.table || ""} disabled={locked} onChange={(e) => update(g.id, { table: e.target.value })} />
                {!locked && (
                  <button data-testid={`guest-delete-${g.id}`} className="p-2 border border-[#D9D3C5] hover:border-red-400 hover:text-red-500" onClick={() => remove(g)}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(g.kind === "child" ? childServices : adultServices).includes("starter") && (
                  <select data-testid={`guest-starter-${g.id}`} className="field" value={g.starter_id || ""} disabled={locked} onChange={(e) => update(g.id, { starter_id: e.target.value })}>
                    <option value="">Entrée…</option>
                    {pool.starter.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                )}
                {(g.kind === "child" ? childServices : adultServices).includes("main") && (
                  <select data-testid={`guest-main-${g.id}`} className="field" value={g.main_id || ""} disabled={locked} onChange={(e) => update(g.id, { main_id: e.target.value })}>
                    <option value="">Plat…</option>
                    {pool.main.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                )}
                {(g.kind === "child" ? childServices : adultServices).includes("dessert") && (
                  <select data-testid={`guest-dessert-${g.id}`} className="field" value={g.dessert_id || ""} disabled={locked} onChange={(e) => update(g.id, { dessert_id: e.target.value })}>
                    <option value="">Dessert…</option>
                    {pool.dessert.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                )}
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid={`guest-allergies-${g.id}`} className="field" placeholder="Allergies déclarées" value={g.allergies || ""} disabled={locked} onChange={(e) => update(g.id, { allergies: e.target.value })} />
                <input data-testid={`guest-diet-${g.id}`} className="field" placeholder="Régime ou demande particulière" value={g.diet_note || ""} disabled={locked} onChange={(e) => update(g.id, { diet_note: e.target.value })} />
              </div>

              <label className="mt-3 flex items-center gap-2 text-xs text-[#697585]">
                <input
                  type="checkbox"
                  data-testid={`guest-exception-${g.id}`}
                  checked={!!g.exception_requested}
                  disabled={locked}
                  onChange={(e) => update(g.id, { exception_requested: e.target.checked })}
                />
                Demander une assiette spécifique (à valider par le restaurant)
                {g.exception_requested && (
                  <span className={wedding.approved_exception_guest_ids?.includes(g.id) ? "text-emerald-700" : "text-[#B8956A]"}>
                    {wedding.approved_exception_guest_ids?.includes(g.id) ? "· validée" : "· en attente"}
                  </span>
                )}
              </label>
            </li>
          );
        })}
      </ul>

      {!visible.length && <p data-testid="guests-empty" className="text-sm text-[#697585]">Aucun invité à afficher.</p>}
    </div>
  );
}
